import React, { useEffect, useState } from "react";
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { getSearchAnalytics } from "../../api/context/analytics";
import { FiBarChart2, FiRefreshCw, FiSearch, FiUsers, FiCalendar, FiInfo } from "react-icons/fi";
import Loader from '../../components/loader/loader';
import toast from "react-hot-toast";

const COLORS = [
    "#6366F1", "#22C55E", "#FACC15", "#F97316",
    "#EC4899", "#06B6D4", "#8B5CF6", "#F43F5E",
];

const AnalyticsDashboard = () => {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const getDefaultDates = () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 7);

        return {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
        };
    };

    const [filters, setFilters] = useState(getDefaultDates());

    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const fetchAnalytics = async (isRefresh = false) => {
        if (isRefresh) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }

        // Prepare filter parameters for REQUEST BODY
        const requestBody = {
            limit: 15,
            startDate: filters.startDate,
            endDate: filters.endDate
        };

        console.log("📅 Analytics params:", requestBody);

        try {
            // Wait for the API call to complete before setting loading to false
            await new Promise((resolve, reject) => {
                getSearchAnalytics(requestBody)((response) => {
                    if (response?.statusCode === 200) {
                        setAnalytics(response?.data);

                        // Calculate number of days and show toast
                        const start = new Date(filters.startDate);
                        const end = new Date(filters.endDate);
                        const diffTime = Math.abs(end - start);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

                        toast.success(`Data fetched successfully for ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`);

                        resolve(response);
                    } else {
                        setAnalytics(null);
                        reject(new Error('Failed to fetch analytics'));
                    }
                });
            });
        } catch (error) {
            console.error("Error fetching analytics:", error);
            setAnalytics(null);
            toast.error('Failed to fetch analytics data');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const handleFilterChange = (key, value) => {
        const today = getTodayDate();

        if (key === 'startDate') {
            // Check if start date is in future
            if (value > today) {
                toast.error('Start date cannot be in the future');
                return;
            }

            // Check if start date is after end date
            if (filters.endDate && value > filters.endDate) {
                toast.error('Start date cannot be after end date');
                return;
            }
        }

        if (key === 'endDate') {
            // Check if end date is in future
            if (value > today) {
                toast.error('End date cannot be in the future');
                return;
            }

            // Check if end date is before start date
            if (filters.startDate && value < filters.startDate) {
                toast.error('End date cannot be before start date');
                return;
            }
        }

        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    // Add useEffect to handle filter changes
    useEffect(() => {
        // Only fetch when we have both dates and it's not the initial default state
        const hasBothDates = filters.startDate && filters.endDate;
        const isDefaultState = JSON.stringify(filters) === JSON.stringify(getDefaultDates());

        if (hasBothDates && !isDefaultState) {
            const timer = setTimeout(() => {
                fetchAnalytics(true);
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [filters]);

    const handleClearFilters = () => {
        const defaultDates = getDefaultDates();
        setFilters(defaultDates);

        // Create request body with default dates directly
        const requestBody = {
            limit: 15,
            startDate: defaultDates.startDate,
            endDate: defaultDates.endDate
        };

        // Manually trigger fetch with default dates
        setTimeout(() => {
            setRefreshing(true);
            getSearchAnalytics(requestBody)((response) => {
                if (response?.statusCode === 200) {
                    setAnalytics(response?.data);
                } else {
                    setAnalytics(null);
                    toast.error('Failed to fetch analytics data');
                }
                setRefreshing(false);
            });
        }, 100);
    };

    const isFilterActive = () => {
        const defaultDates = getDefaultDates();
        return filters.startDate !== defaultDates.startDate || filters.endDate !== defaultDates.endDate;
    };

    if (loading && !refreshing) return (
        <div className="d-flex justify-content-center align-items-center vh-100">
            <Loader />
        </div>
    );

    if (!loading && !analytics) {
        return (
            <div className="flex flex-col items-center justify-center p-10 text-center text-gray-600">
                <FiInfo className="text-5xl mb-3 text-gray-400" />
                <h2 className="font-bold text-2xl">No Data Available</h2>
                <p className="mt-2 text-lg text-gray-500 max-w-md">
                    We couldn't find any analytics to display right now.
                    Try refreshing the page or check back later.
                </p>
            </div>
        );
    }

    return (
        <div className="container-fluid p-4 min-vh-100">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-5">
                <h3 className="mb-0 fw-bolder">Search Analytics</h3>
                <div className="d-flex align-items-center gap-3">
                    {/* Date Filters */}
                    <div className="d-flex align-items-center gap-2">
                        {/* Date Range Selectors */}
                        <div className="d-flex align-items-center gap-2">
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={filters.startDate}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                style={{ width: '140px' }}
                                placeholder="Start Date"
                                max={filters.endDate || getTodayDate()} // Prevent start date from being after end date or future
                            />

                            <div className="text-muted small">to</div>

                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={filters.endDate}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                style={{ width: '140px' }}
                                placeholder="End Date"
                                min={filters.startDate || undefined} // Prevent end date from being before start date
                                max={getTodayDate()} // Prevent future dates
                            />
                        </div>

                        {/* Clear Button */}
                        {isFilterActive() && (
                            <button
                                onClick={handleClearFilters}
                                className="btn btn-sm btn-outline-secondary"
                                disabled={refreshing}
                            >
                                Clear
                            </button>
                        )}
                    </div>

                    {/* Refresh Button */}
                    <button
                        onClick={() => fetchAnalytics(true)}
                        className="btn border border-secondary bg-transparent rounded-3 p-2"
                        style={{
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        disabled={refreshing}
                    >
                        <FiRefreshCw
                            className="text-secondary"
                            size={18}
                            style={{
                                transition: 'transform 0.3s ease-in-out',
                                transform: refreshing ? 'rotate(360deg)' : 'rotate(0deg)',
                                animation: refreshing ? 'spin 1s linear infinite' : 'none'
                            }}
                        />
                    </button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="row g-4 mb-3">
                {[
                    {
                        title: "Total Search Terms",
                        value: analytics.summary.totalSearchTerms,
                        icon: <FiSearch className="text-secondary opacity-75" />
                    },
                    {
                        title: "Total Searches",
                        value: analytics.engagementMetrics.totalSearches,
                        icon: <FiBarChart2 className="text-secondary opacity-75" />
                    },
                    {
                        title: "Active Users",
                        value: analytics.engagementMetrics.activeUsers,
                        icon: <FiUsers className="text-secondary opacity-75" />
                    },
                ].map((card, i) => (
                    <div key={i} className="col-md-4">
                        <div className="card h-80 border border-secondary rounded-3">
                            <div className="card-body p-3 align-items-center position-relative">
                                {/* FI Icon at top right */}
                                <div className="position-absolute top-0 end-0 p-3">
                                    {React.cloneElement(card.icon, {
                                        className: `${card.icon.props.className} fs-5`
                                    })}
                                </div>
                                <h5 className="card-title mb-2 fw-semibold">{card.title}</h5>
                                <p className="fs-4 mb-0" style={{ fontWeight: '600' }}>{card.value}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Charts Grid */}
            <div className="row g-4">
                {/* Popular Searches */}
                <div>
                    <div className="card border-0 rounded-3 h-100">
                        <div className="p-0">
                            <h5 className="fw-bold mb-3 text-start">Popular Searches</h5>
                            <div className="row g-4">
                                {/* Left Column - Chart */}
                                <div className="col-md-8 bg-light rounded-3">
                                    <div style={{ width: "100%", height: 300, padding: 0, marginTop: 40, marginBottom: 30, paddingRight: 30 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                padding={0}
                                                data={analytics.popularSearches}
                                                margin={{ top: 0, right: 0, left: 0, bottom: 20 }}
                                                barCategoryGap="50%"
                                                barGap={20}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis
                                                    dataKey="searchTerm"
                                                    angle={-40}
                                                    textAnchor="end"
                                                    interval={0}
                                                    tick={{ fontSize: 12, fontWeight: 'bold' }}
                                                    padding={{ left: 0, right: 0 }}
                                                />
                                                <YAxis tick={{ fontSize: 12, fontWeight: 'bold' }} />
                                                <Tooltip cursor={{ fill: "transparent" }} />
                                                <Bar
                                                    dataKey="totalSearches"
                                                    fill="#15b8b8ff"
                                                    radius={[6, 6, 0, 0]}
                                                    barSize={40}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                {/* Right Column - Scrollable List */}
                                <div className="col-md-4">
                                    <div
                                        className="border rounded-3 h-100"
                                        style={{
                                            borderColor: '#e9ecef',
                                            maxHeight: 370,
                                            overflowY: 'auto',
                                            backgroundColor: '#f8f9fa'
                                        }}
                                    >
                                        <div className="p-3">
                                            <h6 className="fw-semibold mb-3 text-center text-muted">Top Search Terms</h6>
                                            <div className="d-flex flex-column gap-2">
                                                {analytics.popularSearches.map((search, index) => (
                                                    <div
                                                        key={index}
                                                        className="d-flex justify-content-between align-items-center p-2 rounded-2"
                                                        style={{
                                                            backgroundColor: 'white',
                                                            border: '1px solid #f1f1f1'
                                                        }}
                                                    >
                                                        <div className="d-flex align-items-center">
                                                            <span className="badge bg-light text-dark me-2">
                                                                #{index + 1}
                                                            </span>
                                                            <span
                                                                className="fw-medium text-truncate"
                                                                style={{ maxWidth: '120px' }}
                                                                title={search.searchTerm}
                                                            >
                                                                {search.searchTerm}
                                                            </span>
                                                        </div>
                                                        <span className="fw-bold" style={{ color: '#15b8b8ff' }}>
                                                            {search.totalSearches}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search Trends */}
                <div className="col-lg-12">
                    <div className="card border-0 rounded-3 h-100">
                        <div className="">
                            <h5 className="fw-bold mb-3">Search Trends</h5>
                            <div className="bg-light" style={{ width: "100%", height: 350, }}>
                                <ResponsiveContainer>
                                    <LineChart data={analytics.searchTrends} margin={{ top: 50, bottom: 50, right: 30 }}>
                                        <CartesianGrid strokeDasharray="3 3" />

                                        {/* X & Y axis with smaller font */}
                                        <XAxis
                                            dataKey="date"
                                            angle={-40}
                                            textAnchor="end"
                                            tick={{ fontSize: 12, fontWeight: 'bold' }}
                                        />
                                        <YAxis tick={{ fontSize: 12, fontWeight: 'bold' }} />

                                        {/* Customized tooltip */}
                                        <Tooltip
                                            content={({ active, payload, label }) => {
                                                if (active && payload && payload.length) {
                                                    return (
                                                        <div
                                                            className="p-2 rounded-2 shadow-sm"
                                                            style={{
                                                                background: "#fff",
                                                                border: "1px solid #ddd",
                                                                fontSize: "12px",
                                                                lineHeight: "1.4"
                                                            }}
                                                        >
                                                            <p className="fw-bold mb-1">{label}</p>
                                                            {payload.map((item, index) => (
                                                                <p
                                                                    key={index}
                                                                    style={{ color: item.stroke, margin: 0 }}
                                                                >
                                                                    {item.name}: <strong>{item.value}</strong>
                                                                </p>
                                                            ))}
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />

                                        {/* Lines */}
                                        <Line
                                            type="monotone"
                                            dataKey="dailySearches"
                                            name="Daily Searches"
                                            stroke="#06aebaff"
                                            strokeWidth={2}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="uniqueUsers"
                                            name="Unique Users"
                                            stroke="#ff0000ff"
                                            strokeWidth={2}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Zero Result Searches */}
                <div className="col-12">
                    <div className="card border-0 rounded-3 h-100">
                        <div className="">
                            <h5 className="fw-bold mb-3">Zero Result Searches</h5>
                            {analytics.zeroResultSearches && analytics.zeroResultSearches.length > 0 ? (
                                <div
                                    className="d-flex overflow-auto pb-2 hide-scrollbar"
                                    style={{
                                        whiteSpace: "nowrap",
                                        gap: "1rem",
                                        padding: 0,
                                        margin: 0,
                                    }}
                                >
                                    {analytics.zeroResultSearches.map((item, idx) => (
                                        <div
                                            key={idx}
                                            className="d-inline-flex flex-column justify-content-center align-items-center border rounded-3 p-3 shadow-none"
                                            style={{
                                                minWidth: "150px",
                                                backgroundColor: "#ffffffff",
                                            }}
                                        >
                                            {/* Failures / Attempts on top */}
                                            <span style={{
                                                fontSize: "14px",
                                                fontWeight: "600",
                                                marginBottom: "4px",
                                            }}>
                                                {item.totalAttempts}
                                            </span>

                                            {/* Search term below */}
                                            <span
                                                className="text-center fs-7 fw-semibold text-truncate"
                                                style={{
                                                    maxWidth: "120px", fontSize: "16px",
                                                    color: "#717171ff",
                                                    fontWeight: "700",
                                                    marginBottom: "4px",
                                                }}
                                                title={item.searchTerm}
                                            >
                                                {item.searchTerm}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-muted">No zero-result searches found.</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* User Search Patterns */}
                <div className="col-12">
                    <div className="card border-0 rounded-3 h-100 overflow-auto">
                        <div className="">
                            <h5 className="fw-bold mb-4">User Search Patterns</h5>
                            <div className="table-responsive">
                                <table className="table table-bordered table-hover text-center align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            {"User ID,Total Searches,Total Actions,Avg Relevance,Last Search Date".split(",").map((header, idx) => (
                                                <th key={idx}>{header}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {analytics.userSearchPatterns.map((user, idx) => (
                                            <tr key={idx}>
                                                <td>{user.userId}</td>
                                                <td>{user.totalSearches}</td>
                                                <td>{user.totalSearchActions}</td>
                                                <td>{user.avgRelevance || "N/A"}</td>
                                                <td>{new Date(user.lastSearchDate).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CSS Styles */}
            <style>
                {`
                .hide-scrollbar::-webkit-scrollbar {
                    display: none; /* Chrome, Safari */
                }
                .hide-scrollbar {
                    -ms-overflow-style: none;  /* IE and Edge */
                    scrollbar-width: none;     /* Firefox */
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                `}
            </style>
        </div>
    );
};

export default AnalyticsDashboard;